import { useCallback, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';

import classes from './Auszahlung.module.css';
import {
  GET_USER_STRIPE_STATUS,
  GET_PAYOUT_INFO,
  CREATE_CONNECTED_ACCOUNT,
  CREATE_ONBOARDING_LINK,
  STRIPE_WEB_RETURN_URL,
  STRIPE_WEB_REFRESH_URL,
} from '../../graphql/queries/financeQueries';
import { trackPayoutSetupStarted } from '../../util/tracking';
import { notifyErrorToast } from '../../util/notifyActionError';

const Auszahlung = () => {
  const { user } = useSelector((state) => state?.auth || {});
  const userId = user?.id;
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: stripeData,
    loading: stripeLoading,
    refetch: refetchStripe,
  } = useQuery(GET_USER_STRIPE_STATUS, {
    variables: { id: userId },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: payoutLoading,
    refetch: refetchPayout,
  } = useQuery(GET_PAYOUT_INFO, {
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const [createConnectedAccount, { loading: creatingAccount }] = useMutation(
    CREATE_CONNECTED_ACCOUNT
  );
  const [createOnboardingLink, { loading: creatingLink }] = useMutation(
    CREATE_ONBOARDING_LINK
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnedFromStripe =
      location.state?.stripeReturn === true ||
      urlParams.get('stripe_return') === 'true' ||
      Boolean(urlParams.get('return_url')) ||
      window.location.pathname.includes('stripe-return');

    if (!returnedFromStripe || !userId) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        // getPayoutInfo reads Stripe live and updates the local user Stripe flags.
        await refetchPayout();
        await refetchStripe();
      } catch (err) {
        console.warn('Stripe status refresh after return failed:', err);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [location.state?.stripeReturn, refetchPayout, refetchStripe, userId]);

  const isActionLoading = creatingAccount || creatingLink;

  const stripeUser = stripeData?.getUser;
  const hasAccount = !!stripeUser?.stripeConnectAccountId;
  const isOnboardingComplete = stripeUser?.stripeOnboardingComplete;
  const payoutsEnabled = stripeUser?.stripePayoutsEnabled;
  const chargesEnabled = stripeUser?.stripeChargesEnabled;

  const isFullySetup = Boolean(
    hasAccount && isOnboardingComplete && payoutsEnabled && chargesEnabled
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchStripe(),
        refetchPayout(),
      ]);
    } catch (err) {
      notifyErrorToast('Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', {
        title: 'Auszahlung',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchStripe, refetchPayout]);

  const handleStartSetup = useCallback(async () => {
    if (!userId || !stripeUser) {
      return;
    }

    // Open tab synchronously before any await so Safari treats it as a direct user gesture.
    const stripeTab = window.open('about:blank', '_blank');
    if (stripeTab) {
      // Sever the opener reference (replaces the old noopener flag).
      stripeTab.opener = null;
      try {
        stripeTab.document.write('<p style="font-family:sans-serif;padding:24px">Weiterleitung zu Stripe …</p>');
      } catch (_) {}
    }

    try {
      if (hasAccount && stripeUser.stripeConnectAccountId) {
        const { data } = await createOnboardingLink({
          variables: {
            accountId: stripeUser.stripeConnectAccountId,
            returnUrl: STRIPE_WEB_RETURN_URL,
            refreshUrl: STRIPE_WEB_REFRESH_URL,
          },
        });

        const url = data?.createOnboardingLink?.url;
        if (url) {
          trackPayoutSetupStarted({
            source: 'profile_payout_settings',
            hasAccount: true,
          });
          if (stripeTab) {
            stripeTab.location.replace(url);
          } else {
            window.location.assign(url);
          }
        } else {
          if (stripeTab) stripeTab.close();
          notifyErrorToast('Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', {
            title: 'Auszahlung',
          });
        }
        return;
      }

      const { data } = await createConnectedAccount({
        variables: {
          input: {
            email: stripeUser.email || '',
            country: 'DE',
            returnUrl: STRIPE_WEB_RETURN_URL,
            refreshUrl: STRIPE_WEB_REFRESH_URL,
          },
        },
      });

      const result = data?.createConnectedAccount;
      if (result?.success && result?.onboardingUrl) {
        trackPayoutSetupStarted({
          source: 'profile_payout_settings',
          hasAccount: false,
        });
        if (stripeTab) {
          stripeTab.location.replace(result.onboardingUrl);
        } else {
          window.location.assign(result.onboardingUrl);
        }
      } else if (result?.error) {
        if (stripeTab) stripeTab.close();
        notifyErrorToast(result.error, { title: 'Auszahlungskonto' });
      } else {
        if (stripeTab) stripeTab.close();
        notifyErrorToast('Das Auszahlungskonto konnte nicht erstellt werden.', {
          title: 'Auszahlungskonto',
        });
      }
    } catch (err) {
      if (stripeTab) stripeTab.close();
      notifyErrorToast('Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.', {
        title: 'Auszahlung',
      });
    }
  }, [
    userId,
    stripeUser,
    hasAccount,
    createConnectedAccount,
    createOnboardingLink,
  ]);

  const statusMessage = useMemo(() => {
    if (!hasAccount) {
      return 'Richte dein Stripe Auszahlungskonto ein, um Einnahmen automatisch auf dein Bankkonto auszahlen zu lassen.';
    }
    
    if (stripeUser?.stripeAccountStatus === 'RESTRICTED') {
      return 'Dein Stripe-Konto wurde eingeschränkt. Bitte öffne die Stripe-Einstellungen, um zusätzliche Verifizierungsanforderungen zu erfüllen oder weitere Informationen zu erhalten.';
    }

    if (isFullySetup) {
      return 'Dein Stripe-Konto ist aktiv. Zahlungen und Auszahlungen sind freigeschaltet.';
    }
    
    if (!isOnboardingComplete) {
      return 'Bitte schließe die Verifizierung deines Stripe-Kontos ab, um Auszahlungen zu aktivieren.';
    }
    
    if (!payoutsEnabled || !chargesEnabled) {
      return 'Dein Konto benötigt noch zusätzliche Schritte, um vollständig aktiviert zu werden. Bitte öffne die Stripe-Einstellungen.';
    }
    
    return 'Richte dein Stripe Auszahlungskonto ein, um Einnahmen automatisch auf dein Bankkonto auszahlen zu lassen.';
  }, [
    hasAccount,
    stripeUser?.stripeAccountStatus,
    isFullySetup,
    isOnboardingComplete,
    payoutsEnabled,
    chargesEnabled,
  ]);

  const getStatusConfig = () => {
    if (!hasAccount) return { label: 'Nicht eingerichtet', style: classes.statusDefault };
    if (isFullySetup) return { label: 'Aktiv', style: classes.statusActive };
    if (stripeUser?.stripeAccountStatus === 'RESTRICTED') return { label: 'Eingeschränkt', style: classes.statusRestricted };
    return { label: 'Aktion erforderlich', style: classes.statusPending };
  };

  const statusConfig = getStatusConfig();

  // Check/Pending Icon Components
  const CheckIcon = ({ size = 20 }) => (
    <div className={classes.checkIconContainer} style={{ width: size, height: size }}>
      <span className={classes.checkIconText}>✓</span>
    </div>
  );

  const PendingIcon = ({ size = 20 }) => (
    <div className={classes.pendingIconContainer} style={{ width: size, height: size }}>
      <span className={classes.pendingIconText}>○</span>
    </div>
  );

  if (stripeLoading && !stripeData) {
    return (
      <div className={classes.container}>
        <div className={classes.skeletonContainer}>
          <div className={classes.skeletonHero}>
            <div className={classes.skeletonCircle} />
            <div className={classes.skeletonLine} style={{ width: '60%', marginTop: 20 }} />
            <div className={classes.skeletonLine} style={{ width: '85%', marginTop: 12 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Hero Section */}
      <div className={classes.heroSection}>
        <h1 className='titleSemibold3'>
          {isFullySetup ? 'Auszahlungen aktiv' : 'Auszahlungen einrichten'}
        </h1>
        <p className='regularSmallText'>
          {statusMessage}
        </p>
      </div>

      {/* Status Badge */}
      {hasAccount && (
        <div className={classes.statusContainer}>
          <div className={`${classes.statusBadge} ${statusConfig.style}`}>
            <span className={classes.statusText}>{statusConfig.label}</span>
          </div>
        </div>
      )}

      {/* Progress Card (if account exists) */}
      {hasAccount && (
        <div className={classes.card}>
          <h2 className='titleSemibold3'>Einrichtungsstatus</h2>
          
          <div className={classes.progressItem}>
            <CheckIcon />
            <span className={classes.progressText}>Konto erstellt</span>
          </div>
          
          <div className={classes.progressItem}>
            {isOnboardingComplete ? <CheckIcon /> : <PendingIcon />}
            <span className={`${classes.progressText} ${!isOnboardingComplete ? classes.progressTextPending : ''}`}>
              Verifizierung abgeschlossen
            </span>
          </div>
          
          <div className={classes.progressItem}>
            {chargesEnabled ? <CheckIcon /> : <PendingIcon />}
            <span className={`${classes.progressText} ${!chargesEnabled ? classes.progressTextPending : ''}`}>
              Zahlungsempfang aktiviert
            </span>
          </div>
          
          <div className={classes.progressItem}>
            {payoutsEnabled ? <CheckIcon /> : <PendingIcon />}
            <span className={`${classes.progressText} ${!payoutsEnabled ? classes.progressTextPending : ''}`}>
              Auszahlungen aktiviert
            </span>
          </div>
        </div>
      )}

      {/* Requirements Card (if no account) */}
      {!hasAccount && (
        <div className={classes.card}>
          <h2 className="titleSemibold3">Das brauchst du</h2>
          
          <div className={classes.requirementRow}>
            <div className={classes.requirementIcon}>
              <span className={classes.requirementEmoji}>🪪</span>
            </div>
            <div className={classes.requirementContent}>
              <div className={classes.requirementTitle}>Ausweisdokument</div>
              <div className={classes.requirementSubtitle}>Personalausweis oder Reisepass</div>
            </div>
          </div>
          
          <div className={classes.requirementRow}>
            <div className={classes.requirementIcon}>
              <span className={classes.requirementEmoji}>🏦</span>
            </div>
            <div className={classes.requirementContent}>
              <div className={classes.requirementTitle}>Bankverbindung</div>
              <div className={classes.requirementSubtitle}>IBAN für Auszahlungen</div>
            </div>
          </div>
          
          <div className={classes.requirementRow}>
            <div className={classes.requirementIcon}>
              <span className={classes.requirementEmoji}>📍</span>
            </div>
            <div className={classes.requirementContent}>
              <div className={classes.requirementTitle}>Adresse</div>
              <div className={classes.requirementSubtitle}>Aktuelle Meldeadresse</div>
            </div>
          </div>
        </div>
      )}

      {/* Fee Info Card */}
      <div className={classes.cardBox}>
        <h2 className="titleSemibold3">Gebührenübersicht</h2>
        <div className={classes.feeRow}>
          <span className={classes.feeLabel}>Buchungsbetrag</span>
          <span className={classes.feeValue}>100,00 €</span>
        </div>
        <div className={classes.feeRow}>
          <span className={classes.feeLabelLight}>Plattformgebühr (aktuell 0%)</span>
          <span className={classes.feeValueLight}>-0,00 €</span>
        </div>
        <div className={classes.feeDivider} />
        <div className={classes.feeRow}>
          <span className={classes.feeLabelBold}>Deine Auszahlung</span>
          <span className={classes.feeValueBold}>100,00 €</span>
        </div>
      </div>

      {/* Info Card */}
      <div className={classes.infoCard}>
        <div className={classes.infoTitle}>Sichere Abwicklung</div>
        <p className={classes.infoText}>
          Alle Zahlungen werden über Stripe verarbeitet. Deine Bankdaten sind verschlüsselt und werden nicht bei uns gespeichert.
        </p>
      </div>

      {/* Bottom CTA */}
      <div className={classes.bottomContainer}>
        <button
          type="button"
          className={`${classes.primaryButton} ${(isActionLoading || payoutLoading) ? classes.buttonDisabled : ''}`}
          onClick={handleStartSetup}
          disabled={isActionLoading || payoutLoading || !stripeUser}
        >
          {isActionLoading ? (
            <span>Weiterleiten…</span>
          ) : (
            <span>
              {isFullySetup ? 'Einstellungen öffnen' : hasAccount ? 'Fortsetzen' : 'Jetzt einrichten'}
            </span>
          )}
        </button>

        {hasAccount && (
          <button
            type="button"
            className={`${classes.textButton} ${(payoutLoading || isRefreshing) ? classes.buttonDisabled : ''}`}
            onClick={handleRefresh}
            disabled={payoutLoading || isRefreshing}
          >
            <span className={classes.textButtonText}>
              {(payoutLoading || isRefreshing)
                ? 'Status wird aktualisiert...'
                : 'Status aktualisieren'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Auszahlung;
